import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { TagType } from "@generated/client/graphql";
import {
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type FC,
    type KeyboardEvent,
} from "react";
import { useCreateTag } from "./data";
import { useTagListEditorTranslations } from "./i18n";

const NewTagDropdownItem: FC<{
    type: TagType;
    onSaveNewTagSettled: (tag: { name: string; id?: string }) => void;
}> = ({ type, onSaveNewTagSettled }) => {
    const { t, keys } = useTagListEditorTranslations();

    const [isEditing, setIsEditing] = useState(false);
    const [newTagName, setNewTagName] = useState<string>("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { mutate } = useCreateTag();
    const handleNewTagNameChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setNewTagName(e.target.value?.trimStart());
        textareaRef.current?.focus();
    };
    const handleSaveNewTagName = () => {
        if (!newTagName) return;
        mutate(
            { type, name: newTagName },
            {
                onSettled(data) {
                    onSaveNewTagSettled(
                        data?.createTag ?? { name: newTagName },
                    );
                    setNewTagName("");
                    setIsEditing(false);
                },
            },
        );
    };

    useEffect(() => {
        if (isEditing) {
            textareaRef.current?.focus();
        }
    }, [isEditing]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        e.stopPropagation();
        if (e.key === "Enter") {
            textareaRef.current?.blur();
            handleSaveNewTagName();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            setIsEditing(false);
        }
    };

    return isEditing ? (
        <FlexRow
            h="9"
            px="0"
            py="0.5"
            alignItems="center"
            justifyContent="center"
            className="relative rounded-lg focus:outline-none"
        >
            <textarea
                autoFocus
                value={newTagName}
                onChange={handleNewTagNameChange}
                onKeyDown={handleKeyDown}
                ref={textareaRef}
                className="h-full w-full resize-none rounded-lg border-secondary focus:border-secondary focus:ring-0"
            />
            <Button
                variant="flatPrimary"
                size="xs"
                className="absolute right-2"
                disabled={!newTagName}
                onClick={handleSaveNewTagName}
            >
                <Icon name="arrowRight" size="xs" />
            </Button>
        </FlexRow>
    ) : (
        <FlexRow
            className="hover:bg-green-50 rounded-lg cursor-pointer flex-grow px-2 py-1.5 focus:outline-none space-x-2.5 flex items-center"
            onClick={() => {
                setIsEditing(true);
            }}
        >
            <Icon name="plus" size="xs" className="text-green" />
            <Text variant="body-sm">
                {t(keys.add_new_label, { context: type })}
            </Text>
        </FlexRow>
    );
};

export default NewTagDropdownItem;
